import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Bus, ArrowRight, Clock, Star } from "lucide-react";
import api from "@/lib/api";
import { inr, prettyDate } from "@/lib/format";
import Shell from "@/components/Shell";
import SearchWidget from "@/components/SearchWidget";

export default function BusResults() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const from = params.get("from"), to = params.get("to"), date = params.get("date");
  const seats = Number(params.get("pax") || 1);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!from || !to || !date) { setLoading(false); return; }
    setLoading(true);
    api.get(`/buses/search?from=${from}&to=${to}&date=${date}&pax=${seats}`)
      .then((r) => setResults(r.data.results)).finally(() => setLoading(false));
  }, [from, to, date, seats]);

  const select = (b) => navigate("/checkout", { state: {
    type: "bus", item_id: b.id, title: `${b.from_name} → ${b.to_name}`, image: null,
    amount: b.price * seats, currency: "inr",
    details: { from: b.from, to: b.to, from_name: b.from_name, to_name: b.to_name, date: b.date, operator: b.operator, type: b.type, depart: b.depart, arrive: b.arrive, price: b.price, seats },
    summary: [{ k: "Operator", v: b.operator }, { k: "Type", v: b.type }, { k: "Date", v: prettyDate(b.date) }, { k: "Depart", v: b.depart }, { k: "Seats", v: seats }, { k: "Fare/seat", v: inr(b.price) }],
    icon: "bus",
  }});

  return (
    <Shell>
      <section className="max-w-6xl mx-auto px-5 sm:px-8 pt-8"><SearchWidget initialTab="buses" /></section>
      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-8">
        <h1 className="font-display text-3xl text-foreground mb-1">Buses {from} → {to}</h1>
        <p className="text-muted-foreground text-sm mb-6">{prettyDate(date)} · {seats} seat(s)</p>
        {loading ? (
          <div className="space-y-3">{[...Array(4)].map((_, i) => <div key={i} className="h-24 rounded-xl bg-secondary/60 animate-pulse" />)}</div>
        ) : results.length === 0 ? (
          <p className="text-muted-foreground py-16 text-center">No buses found. Try different cities.</p>
        ) : (
          <div className="space-y-3" data-testid="bus-results">
            {results.map((b, i) => (
              <div key={b.id} className="rounded-xl border border-primary/10 bg-card p-5 flex flex-wrap items-center gap-6 justify-between" data-testid={`bus-${i}`}>
                <div className="flex items-center gap-4">
                  <span className="grid place-items-center w-11 h-11 rounded-lg bg-secondary text-accent"><Bus size={20} /></span>
                  <div>
                    <p className="font-semibold text-foreground">{b.operator}</p>
                    <p className="text-xs text-muted-foreground">{b.type} · <Star size={11} className="inline fill-accent text-accent" /> {b.rating}</p>
                  </div>
                </div>
                <div className="flex items-center gap-5 text-center">
                  <div><p className="font-display text-2xl text-foreground">{b.depart}</p><p className="text-xs text-muted-foreground">{b.from}</p></div>
                  <div className="text-muted-foreground text-xs"><Clock size={13} className="mx-auto mb-1" />{b.duration}</div>
                  <div><p className="font-display text-2xl text-foreground">{b.arrive}</p><p className="text-xs text-muted-foreground">{b.to}</p></div>
                </div>
                <div className="text-right">
                  <p className="font-display text-2xl text-foreground">{inr(b.price)}</p>
                  <button onClick={() => select(b)} data-testid={`select-bus-${i}`} className="mt-1 inline-flex items-center gap-1.5 rounded-full bg-accent text-accent-foreground px-5 py-2 text-sm font-semibold hover:-translate-y-px transition-transform">Select <ArrowRight size={14} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </Shell>
  );
}
